import { useEffect } from 'react'
import { createClient } from '@connectrpc/connect'
import { useQuery } from '@tanstack/react-query'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import type { ServerEvent } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/event/v1/event.v1_pb'
import { EventService } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/event/v1/event.v1_pb'

import { authTransport } from './transport'

const eventClient = createClient(EventService, authTransport)

const listeners: Set<(event: ServerEvent) => void> = new Set()
const request = {
  customerSubscription: {},
}
const queryKey = ['server_event_subscription']

export function useServerEvents(listener: (event: ServerEvent) => void) {
  useEffect(() => {
    // make a local copy of the listener to protect against an edge case where the useServerEvents hook
    // is used in multiple places with the same listener reference. Example: useServerEvents(listenerA) + useServerEvents(listenerA)
    // in this case, we want to maintain multiple copies in the global set to avoid stopping all subscriptions if only one
    // hook is unmounted
    const listenerCopy = (event: ServerEvent) => listener(event)
    listeners.add(listenerCopy)
    return () => {
      listeners.delete(listenerCopy)
    }
  }, [listener])

  const enabled = useAuthenticated() === 'TRUE'

  useQuery({
    queryKey: queryKey,
    throwOnError: false,
    enabled,
    queryFn: async ({ signal }) => {
      const events = eventClient.subscribeFixed(request, {
        signal,
        timeoutMs: 0,
      })
      for await (const event of events) {
        const eventCase = event.event.case
        // filter out PING
        if (eventCase && eventCase !== 'ping') {
          listeners.forEach(cb => {
            try {
              cb(event)
            } catch (error) {
              console.error('Error in server event listener:', error)
            }
          })
        }
      }

      return {}
    },
  })
}
